import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { dialogsAPI, userAPI, summariesAPI } from '../services/api';
import CoachSidebar from '../components/Dashboard/CoachSidebar';
import SummaryModal from '../components/Modals/SummaryModal';
import EditAvatarModal from '../components/Modals/EditAvatarModal';
import EditPromptModal from '../components/Modals/EditPromptModal';

const ClientHistory = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [client, setClient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Calculate skip for backend pagination
        const skip = (currentPage - 1) * itemsPerPage;

        const [dialogsResponse, clientResponse, summariesResponse] = await Promise.all([
          // Fetch dialogs for the specific client with pagination
          dialogsAPI.getAll({ 
            user_id: clientId, 
            has_messages: true,
            skip: skip,
            limit: itemsPerPage
          }),
          userAPI.getById(clientId),
          summariesAPI.getAll({ user_id: clientId })
        ]);
        
        const rawDialogsData = dialogsResponse.data;
        console.log('ClientHistory Debug - Raw API Response:', rawDialogsData);
        
        const normalizedClient = clientResponse.data?.user || clientResponse.data;
        setClient(normalizedClient);
        
        // Handle both simple array and paginated object { items, total }
        const allDialogs = rawDialogsData.items || (Array.isArray(rawDialogsData) ? rawDialogsData : []);
        const total = rawDialogsData.total !== undefined ? rawDialogsData.total : (Array.isArray(rawDialogsData) ? rawDialogsData.length : 0);
        
        setTotalCount(total);

        console.log(`ClientHistory Debug - Extracted ${allDialogs.length} dialogs total.`);
        
        // Group summaries by dialog_id
        const summariesByDialog = {};
        const summariesItems = Array.isArray(summariesResponse.data) 
          ? summariesResponse.data 
          : (summariesResponse.data?.items || summariesResponse.data?.data?.items || []);
        
        summariesItems.forEach(s => {
          if (s.dialog_id) {
            summariesByDialog[s.dialog_id] = s;
          }
        });
        
        // Normalize the data format from the API
        const formattedSessions = allDialogs.map(dialog => {
          const createdAt = new Date(dialog.created_at || dialog.updated_at);
          const source = (dialog.metadata?.source || 'chat').toLowerCase();
          
          let type = 'Chat';
          let icon = 'forum';
          let colorClass = 'bg-emerald-500/10 text-emerald-400';
          
          if (source.includes('voice')) {
            type = 'Voice';
            icon = 'mic';
            colorClass = 'bg-[var(--tangerine)]/10 text-[var(--tangerine)]';
          } else if (source.includes('video')) {
            type = 'Video';
            icon = 'videocam';
            colorClass = 'bg-blue-500/10 text-blue-400';
          }
          
          return {
            id: dialog.id,
            dateObj: createdAt,
            date: createdAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
            time: createdAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
            duration: dialog.metadata?.duration || 'Variable',
            topic: dialog.metadata?.topic || `${type} Session`,
            type: type,
            icon: icon,
            colorClass: colorClass,
            summary: summariesByDialog[dialog.id] || null
          };
        });

        // Sort by date desc using date object
        formattedSessions.sort((a, b) => b.dateObj - a.dateObj);
        
        setSessions(formattedSessions);
      } catch (error) {
        console.error('Failed to fetch client history:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (clientId) fetchData();
  }, [clientId, currentPage]);

  // Pagination logic
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const currentSessions = sessions; // Already paginated from backend

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="relative flex size-full min-h-screen flex-col bg-[var(--bg-main)] group/design-root overflow-x-hidden font-display text-[var(--text-primary)]">
      
      <EditAvatarModal isOpen={isAvatarModalOpen} onClose={() => setIsAvatarModalOpen(false)} currentAvatar="" />
      <EditPromptModal isOpen={isPromptModalOpen} onClose={() => setIsPromptModalOpen(false)} />

      <div className="layout-container flex h-full grow flex-col">
        <div className="px-4 sm:px-6 md:px-8 lg:px-12 flex flex-1 justify-center py-5 w-full">
          <div className="layout-content-container flex flex-col lg:flex-row w-full max-w-[1200px] flex-1 gap-8">
            
            <main className="flex-1 flex flex-col">
              <header className="mb-8">
                <nav className="flex items-center gap-2 text-[10px] font-black text-[var(--text-secondary)]/40 uppercase tracking-[0.2em] mb-4">
                  <Link to="/coach/dashboard" className="hover:text-[var(--tangerine)] transition-colors">Dashboard</Link>
                  <span className="material-symbols-outlined text-xs">chevron_right</span>
                  <span className="text-[var(--text-secondary)]/60">Client History</span>
                </nav>
                <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] tracking-tight mb-2">
                  {client ? `Sessions with ${client.name}` : 'Client Sessions'}
                </h1>
                <p className="text-[var(--text-secondary)] text-sm">Review full transcript history and session details.</p>
              </header>

              <div className="space-y-4">
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center py-24 opacity-40">
                    <div className="w-12 h-12 border-4 border-[var(--tangerine)] border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="font-black uppercase tracking-widest text-xs">Loading sessions...</p>
                  </div>
                ) : currentSessions.length > 0 ? (
                  <>
                    {currentSessions.map((session) => (
                      <div 
                        key={session.id} 
                        onClick={() => navigate(`/coach/history/${session.id}`)}
                        className="bg-[var(--surface-white)] rounded-3xl p-6 border border-[var(--border-light)] shadow-xl shadow-black/5 flex flex-col sm:flex-row items-center gap-6 transition-all hover:border-[var(--tangerine)]/30 group cursor-pointer animate-in fade-in slide-in-from-bottom-2"
                      >
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${session.colorClass}`}>
                          <span className="material-symbols-outlined text-3xl">
                            {session.icon}
                          </span>
                        </div>
                        
                        <div className="flex-1 text-center sm:text-left min-w-0">
                          <div className="flex flex-wrap justify-center sm:justify-start items-center gap-x-3 gap-y-1 mb-1">
                            <h3 className="font-bold text-[var(--text-primary)] text-lg">{session.topic}</h3>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-widest ${session.colorClass} border border-current opacity-70`}>
                              {session.type}
                            </span>
                          </div>
                          <div className="flex flex-wrap justify-center sm:justify-start items-center gap-4 text-sm text-[var(--text-secondary)]/60">
                            <div className="flex items-center gap-1">
                              <span className="material-symbols-outlined text-base">calendar_today</span>
                              {session.date}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="material-symbols-outlined text-base">schedule</span>
                              {session.time}
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          {session.summary && (
                            <button 
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setSelectedSummary(session.summary); 
                                setIsSummaryModalOpen(true); 
                              }}
                              className="bg-[var(--tangerine)]/10 border border-[var(--tangerine)]/20 hover:bg-[var(--tangerine)]/20 text-[var(--tangerine)] font-bold px-4 py-3 rounded-2xl transition-all flex items-center gap-2 shadow-sm hover:shadow-md"
                            >
                              <span className="material-symbols-outlined text-xl">summarize</span>
                              Summary
                            </button>
                          )}
                          <button className="bg-[var(--surface-white)] border border-[var(--border-light)] hover:bg-[var(--off-white)] text-[var(--text-secondary)] font-bold px-6 py-3 rounded-2xl transition-all flex items-center gap-2 group-hover:shadow-md group-hover:text-[var(--tangerine)] group-hover:border-[var(--tangerine)]/30">
                            <span className="material-symbols-outlined text-xl">description</span>
                            View Transcript
                          </button>
                        </div>
                      </div>
                    ))}

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="flex justify-center items-center gap-2 mt-12 py-4">
                        <button
                          onClick={() => paginate(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="w-10 h-10 rounded-xl border border-[var(--border-light)] flex items-center justify-center text-[var(--text-secondary)] hover:border-[var(--tangerine)] hover:text-[var(--tangerine)] transition-all disabled:opacity-30 disabled:pointer-events-none"
                        >
                          <span className="material-symbols-outlined text-xl">chevron_left</span>
                        </button>
                        
                        <div className="flex items-center gap-1 px-2">
                          {[...Array(totalPages)].map((_, i) => (
                            <button
                              key={i + 1}
                              onClick={() => paginate(i + 1)}
                              className={`w-10 h-10 rounded-xl font-bold text-sm transition-all ${
                                currentPage === i + 1
                                  ? 'bg-[var(--tangerine)] text-white shadow-lg shadow-orange-500/20 scale-110'
                                  : 'text-[var(--text-secondary)] hover:bg-[var(--off-white)]'
                              }`}
                            >
                              {i + 1}
                            </button>
                          ))}
                        </div>

                        <button
                          onClick={() => paginate(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="w-10 h-10 rounded-xl border border-[var(--border-light)] flex items-center justify-center text-[var(--text-secondary)] hover:border-[var(--tangerine)] hover:text-[var(--tangerine)] transition-all disabled:opacity-30 disabled:pointer-events-none"
                        >
                          <span className="material-symbols-outlined text-xl">chevron_right</span>
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-[var(--surface-white)] rounded-[2.5rem] p-12 text-center border border-dashed border-gray-300 shadow-sm">
                    <div className="w-16 h-16 bg-[var(--off-white)] rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300">
                      <span className="material-symbols-outlined text-4xl">history</span>
                    </div>
                    <h3 className="text-xl font-bold text-[var(--text-primary)] mb-2">No sessions yet</h3>
                    <p className="text-[var(--text-secondary)] text-sm max-w-xs mx-auto">This client hasn't started any conversations with your avatar yet.</p>
                  </div>
                )}
              </div>
            </main>

            <SummaryModal 
              isOpen={isSummaryModalOpen} 
              onClose={() => setIsSummaryModalOpen(false)} 
              summary={selectedSummary} 
            />

            <CoachSidebar 
              onOpenAvatarModal={() => setIsAvatarModalOpen(true)}
              onOpenPromptModal={() => setIsPromptModalOpen(true)}
              onOpenSettingsModal={() => { window.location.href = '/coach/settings' }}
            />
            
          </div>
        </div>
      </div>
    </div>
  );
};
export default ClientHistory;
